// Upgrades [data-scene] drawings to live Three.js models, but only when
// the visitor's device and settings can afford it. The ~170 KB 3D chunk
// is fetched after the page has loaded and only once a scene is near
// the viewport, so it never competes with the page's first paint.
let stage;

// Scenes with a 3D model (keep in step with `models` in three/models.js);
// the rest keep their drawing, and pages with none skip the 3D chunk.
const MODELS = new Set(['aio', 'monitor', 'laptop', 'desktop', 'workstation', 'minipc', 'thinclient', 'panel',
  'cctv', 'cctv-bullet', 'cctv-dome', 'cctv-ptz', 'drone', 'pcb', 'chip', 'storage', 'network', 'mobile']);

function capable() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return false;
  const conn = navigator.connection;
  if (conn && (conn.saveData || /(^|-)2g$/.test(conn.effectiveType || ''))) return false;
  if (navigator.deviceMemory && navigator.deviceMemory < 4) return false;
  try {
    return !!document.createElement('canvas').getContext('webgl2');
  } catch {
    return false;
  }
}

const afterLoad = () => new Promise((resolve) => {
  const idle = () => (window.requestIdleCallback ? requestIdleCallback(resolve, { timeout: 1500 }) : setTimeout(resolve, 200));
  if (document.readyState === 'complete') idle();
  else window.addEventListener('load', idle, { once: true });
});

let observer;

export function initScenes(root) {
  const scenes = [...(root.querySelectorAll?.('[data-scene]:not([data-scene-init])') ?? [])].filter((el) => MODELS.has(el.dataset.scene));
  if (!scenes.length || !capable()) return;
  observer ??= new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      observer.unobserve(entry.target);
      mount(entry.target);
    }
  }, { rootMargin: '300px 0px' });
  for (const el of scenes) {
    el.dataset.sceneInit = '';
    observer.observe(el);
  }
}

async function mount(el) {
  await afterLoad();
  stage ??= import('./three/stage.js');
  try {
    const { mountScene } = await stage;
    mountScene(el, el.dataset.scene, el.dataset.sceneMode);
  } catch (err) {
    // The drawing stays; a failed 3D upgrade must never break the page.
    console.warn('3D scene unavailable:', err);
  }
}
